import React, { useState, useEffect, useCallback } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Typography,
    Box,
    Avatar,
    TextField,
    TablePagination,
    Modal,
    IconButton,
} from "@mui/material";
import { format } from "date-fns";
import { colors } from "components/Theme/WebTheme";
import ElevatedButton from "components/Button/ElevatedButton";
import ConfirmationDialog from "components/Dialog/Confirmation";
import LoadingProgress from "components/Dialog/LoadingProgress";
import CloseIcon from "@mui/icons-material/Close";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import DownloadIcon from "@mui/icons-material/Download";
import { api } from "common";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";
import AddCarDialog from "components/Dialog/AddCar";
import EditCarDialog from "components/Dialog/EditCar";
import { firebase } from "common/src/config/configureFirebase";
import { ref, onValue, update, remove } from "firebase/database";

const useStyles = makeStyles((theme) => ({
    checkbox: {
        cursor: "default",
        "&.Mui-checked": {
            color: colors.MAIN_COLOR,
        },
    },
    modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    modalPaper: {
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        padding: 0,
        borderRadius: 12,
        width: "90%",
        maxWidth: 800,
        maxHeight: "80vh",
        overflow: "auto",
    },
    modalHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: theme.spacing(3),
        borderBottom: "1px solid #e0e0e0",
    },
    modalContent: {
        padding: theme.spacing(3),
    },
    imageContainer: {
        position: "relative",
        textAlign: "center",
    },
    carImage: {
        maxWidth: "100%",
        maxHeight: "500px",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
    navigationButton: {
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 1)",
        },
    },
    prevButton: {
        left: 16,
    },
    nextButton: {
        right: 16,
    },
    imageCounter: {
        position: "absolute",
        top: 16,
        right: 16,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        color: "white",
        padding: "4px 8px",
        borderRadius: "12px",
        fontSize: "0.875rem",
    },
}));

const CarsListMultiService = ({
    dataDrivers,
    drivers,
    dataCars,
    onDataCarsChange,
    t,
    onHandleMultiImage,
}) => {
    const { editCar, editUser } = api;
    const dispatch = useDispatch();
    const classes = useStyles();
    const settings = useSelector((state) => state.settingsdata.settings);
    const addons = useSelector((state) => state.addondata.data);

    const auth = useSelector((state) => state.auth);

    const [cars, setCars] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [showAddCarDialog, setShowAddCarDialog] = useState(false);
    const [showEditCarDialog, setShowEditCarDialog] = useState(false);
    const [showLoadingProgress, setShowLoadingProgress] = useState(false);
    const [selectedCar, setSelectedCar] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(5);
    const [loading, setLoading] = useState(false);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [selectedCarImages, setSelectedCarImages] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Function to fetch cars directly from Firebase with proper cleanup
    const fetchCarsFromFirebase = useCallback(() => {
        console.log("🔄 Setting up Firebase listener...");
        setLoading(true);

        if (!auth.profile || !auth.profile.uid) {
            console.log("No auth profile or uid available");
            setLoading(false);
            return;
        }

        try {
            console.log("Fetching cars from Firebase...");

            const { carsRef } = firebase;
            const userInfo = auth.profile;

            console.log("Firebase object:", firebase);
            console.log("carsRef function:", carsRef);
            console.log("User info:", userInfo);

            // Get multiservice settings
            const isMultiService =
                addons?.multi_service && settings?.multi_service;
            console.log("CarsListMultiService isMultiService:", isMultiService);

            // Get cars reference based on user type
            console.log("User UID:", userInfo.uid);
            console.log("User type:", userInfo.usertype);

            // For admin users, we want to see all cars
            const userType =
                userInfo.usertype === "admin"
                    ? "fleetadmin"
                    : userInfo.usertype;
            let carsReference;

            if (userInfo.usertype === "admin") {
                // Admin can see all cars
                carsReference = ref(firebase.database, "cars");
            } else {
                carsReference = carsRef(userInfo.uid, userType);
            }
            console.log("carsReference", carsReference);

            console.log("Setting up onValue listener...");
            const unsubscribe = onValue(
                carsReference,
                (snapshot) => {
                    console.log("🔥 Firebase snapshot received");
                    console.log("Firebase snapshot", snapshot.val());
                    if (!snapshot.val()) {
                        console.log("No cars found in Firebase");
                        setCars([]);
                        setLoading(false);
                        return;
                    }

                    let data = snapshot.val();
                    console.log("Raw data from Firebase:", data);
                    console.log("Data type:", typeof data);
                    console.log("Is array:", Array.isArray(data));

                    // Log sample car data structure
                    if (data && typeof data === "object") {
                        const sampleKeys = Object.keys(data).slice(0, 2);
                        sampleKeys.forEach((key) => {
                            console.log(`📋 Sample car ${key}:`, data[key]);
                        });
                    }

                    // Handle different data structures
                    let arr = [];
                    if (Array.isArray(data)) {
                        // Data is already an array - this is the correct structure from glory.js
                        console.log(
                            "Data is array, processing as glory.js structure"
                        );
                        arr = data; // Keep as array of objects with car_id as key
                    } else if (data && typeof data === "object") {
                        // Data is an object, convert to array
                        console.log("Data is object, converting to array");
                        arr = Object.keys(data).map((key) => {
                            return { [key]: data[key] };
                        });
                    } else {
                        console.log("Data is neither array nor object:", data);
                        arr = [];
                    }

                    console.log("Processed array:", arr);
                    console.log("Array length:", arr.length);

                    // Filter cars based on multiservice setting
                    if (isMultiService) {
                        // If multiservice is TRUE, only show cars with for_multi_service key
                        arr = arr.filter((carGroupObj) => {
                            try {
                                const carId = Object.keys(carGroupObj || {})[0];
                                const carGroup = carGroupObj[carId];
                                const hasForMultiService =
                                    carGroup?.for_multi_service === true;

                                console.log(
                                    `CarsListMultiService filtering car ${carId}:`,
                                    {
                                        for_multi_service:
                                            carGroup?.for_multi_service,
                                        hasForMultiService: hasForMultiService,
                                        isMultiService: isMultiService,
                                        shouldShow: hasForMultiService,
                                    }
                                );

                                return hasForMultiService;
                            } catch (filterError) {
                                console.error(
                                    "CarsListMultiService filter error:",
                                    filterError
                                );
                                return false;
                            }
                        });
                        console.log(
                            "CarsListMultiService filtered array length:",
                            arr.length
                        );
                    } else {
                        // If multiservice is FALSE, show cars WITHOUT for_multi_service key (traditional cars)
                        arr = arr.filter((carGroupObj) => {
                            try {
                                const carId = Object.keys(carGroupObj || {})[0];
                                const carGroup = carGroupObj[carId];
                                const hasForMultiService =
                                    carGroup?.for_multi_service === true;

                                console.log(
                                    `CarsListMultiService filtering car ${carId}:`,
                                    {
                                        for_multi_service:
                                            carGroup?.for_multi_service,
                                        hasForMultiService: hasForMultiService,
                                        isMultiService: isMultiService,
                                        shouldShow: !hasForMultiService,
                                    }
                                );

                                return !hasForMultiService; // Show cars WITHOUT for_multi_service
                            } catch (filterError) {
                                console.error(
                                    "CarsListMultiService filter error:",
                                    filterError
                                );
                                return false;
                            }
                        });
                        console.log(
                            "CarsListMultiService filtered array length:",
                            arr.length
                        );
                    }

                    if (arr.length > 0) {
                        // Sort descending by createdAt
                        arr.sort((a, b) => {
                            const aKey = Object.keys(a)[0];
                            const bKey = Object.keys(b)[0];
                            return (
                                (b[bKey]?.createdAt || 0) -
                                (a[aKey]?.createdAt || 0)
                            );
                        });
                        console.log("Sorted array (newest first):", arr);

                        // Log first few cars with their creation dates for verification
                        console.log(
                            "📅 First 3 cars by creation date:",
                            arr.slice(0, 3).map((car) => {
                                const key = Object.keys(car)[0];
                                return {
                                    id: key,
                                    createdAt: car[key]?.createdAt,
                                    createdDate: car[key]?.createdAt
                                        ? new Date(
                                              car[key].createdAt
                                          ).toLocaleString()
                                        : "No date",
                                    driver: car[key]?.driver,
                                    vehicleNumber: car[key]?.vehicleNumber,
                                };
                            })
                        );
                    } else {
                        console.log("Array is empty, no cars to sort");
                    }

                    console.log("Setting cars state with:", arr);
                    setCars(arr);

                    // If onDataCarsChange is provided, call it with the data
                    if (typeof onDataCarsChange === "function") {
                        console.log("Calling onDataCarsChange with:", arr);
                        onDataCarsChange(arr);
                    }

                    setLoading(false);
                },
                (error) => {
                    console.error("Error fetching cars from Firebase:", error);
                    console.error("Error details:", error.message, error.code);
                    setLoading(false);
                }
            );

            // Return cleanup function
            return unsubscribe;
        } catch (error) {
            console.error("Error in fetchCarsFromFirebase:", error);
            setLoading(false);
            return null;
        }
    }, [auth.profile, onDataCarsChange, settings, addons]);

    // Fetch cars on component mount with proper cleanup
    useEffect(() => {
        console.log("Auth profile:", auth.profile);
        console.log("Drivers data:", drivers);
        console.log("Data drivers:", dataDrivers);

        const unsubscribe = fetchCarsFromFirebase();

        // Cleanup function
        return () => {
            if (unsubscribe && typeof unsubscribe === "function") {
                console.log("🧹 Cleaning up Firebase listener");
                unsubscribe();
            }
        };
    }, [fetchCarsFromFirebase, auth.profile, drivers, dataDrivers]);

    // Function to delete car from Firebase
    const deleteCarFromFirebase = async (carId) => {
        try {
            console.log("🗑️ Starting car deletion process for ID:", carId);

            const { carEditRef } = firebase;

            // Use the same reference structure as editCar action
            const carRef = carEditRef(carId);
            console.log("📍 Car reference path:", carRef.toString());

            // Delete from Firebase first
            console.log("🔥 Deleting from Firebase...");
            await remove(carRef);
            console.log("✅ Firebase deletion successful");

            // Also dispatch the Redux action to ensure consistent state management
            console.log("🔄 Dispatching Redux delete action...");
            dispatch(editCar({ id: carId }, "Delete"));
            console.log("✅ Redux action dispatched");

            console.log("🎉 Car deleted successfully from Firebase and Redux");
            return true;
        } catch (error) {
            console.error("❌ Error in deleteCarFromFirebase:", error);
            console.error("Error details:", {
                message: error.message,
                code: error.code,
                stack: error.stack,
            });
            return false;
        }
    };

    const handleApprove = async (carGroup, field, value) => {
        const checked = value;

        // Find the car group in cars data
        const updatedCars = cars.map((carGroupObj) => {
            const carId = Object.keys(carGroupObj)[0];
            const carGroupData = carGroupObj[carId];

            if (carId === carGroup.car_id) {
                // Update group level
                const updatedCarGroup = { ...carGroupData, [field]: checked };

                // If multi-service, also update individual cars
                if (Array.isArray(carGroupData.cars)) {
                    const updatedCars = carGroupData.cars.map((car) => {
                        return { ...car, [field]: checked };
                    });
                    updatedCarGroup.cars = updatedCars;
                }

                return { [carId]: updatedCarGroup };
            }
            return carGroupObj;
        });

        const updatedCar = updatedCars.find(
            (item) => Object.keys(item)[0] === carGroup.car_id
        );

        if (updatedCar && typeof onDataCarsChange === "function") {
            const carId = Object.keys(updatedCar)[0];
            const carData = updatedCar[carId];

            let driver = dataDrivers.find((d) => d.uid === carData.driver);
            let newUpdatedCar;

            if (field === "active") {
                newUpdatedCar = { ...carData, active: checked };

                // Update driver's carType and category for backend compatibility
                if (driver) {
                    if (checked) {
                        // If activating, set driver's carType and category
                        driver.carType = carData.carType || "";
                        driver.category = carData.category || "";
                    } else {
                        // If deactivating, clear driver's carType and category
                        driver.carType = "";
                        driver.category = "";
                    }

                    // Ensure all required fields are present and not undefined
                    const updatedDriver = {
                        ...driver,
                        carType: driver.carType || "",
                        category: driver.category || "",
                        carApproved:
                            driver.carApproved !== undefined
                                ? driver.carApproved
                                : true,
                    };

                    uploadDataDrivers(updatedDriver);
                }

                // Firebase update will be handled by uploadDataCars
            } else if (field === "approved") {
                // Handle approved status change
                let carApproved = carData.approved;
                if (
                    carApproved === undefined ||
                    carApproved === null ||
                    carApproved === false
                ) {
                    carApproved = checked;
                }
                newUpdatedCar = { ...carData, approved: carApproved };

                // Only update driver's carApproved if multi_service is not enabled
                // For multi-service, carApproved is always true since approval is handled at car level
                if (!addons.multi_service) {
                    driver = { ...driver, carApproved: checked };
                } else {
                    driver = { ...driver, carApproved: true };
                }

                // Firebase update will be handled by uploadDataCars
            }

            console.log("newUpdatedCar", newUpdatedCar);

            // Send the car object with the correct structure
            const carToUpdate = {
                ...carGroup,
                [field]: checked,
                field: field,
                groupId: carGroup.car_id,
                car_id: carGroup.car_id,
                driver: carData.driver,
                id: carData.id,
            };

            await uploadDataCars(carToUpdate);
        }
    };

    const uploadDataDrivers = useCallback(
        (driver) => {
            dispatch(editUser(driver.id, driver));
        },
        [dispatch, editUser]
    );

    useEffect(() => {
        if (dataDrivers.length > 0 && cars.length > 0) {
            for (let i = 0; i < dataDrivers.length; i++) {
                const driver = dataDrivers[i];
                const dataCarsApproved = cars.filter((item) => {
                    const carId = Object.keys(item)[0];
                    return item[carId].driver === driver.uid;
                });
                console.log("dataCarsApproved", dataCarsApproved);
                const ifExistCarApproved = dataCarsApproved.find((item) => {
                    const carId = Object.keys(item)[0];
                    const carData = item[carId];
                    if (carData.cars && Array.isArray(carData.cars)) {
                        const existCars = carData.cars.find(
                            (car) => car.approved === true
                        );
                        return existCars;
                    } else {
                        // Handle single car (non-multi-service)
                        return carData.approved === true;
                    }
                });

                let carApproved = false;
                let carType = "";
                if (dataCarsApproved.length > 0) {
                    const carId = Object.keys(dataCarsApproved[0])[0];
                    const driverCars = dataCarsApproved[0][carId].cars;
                    if (
                        driverCars &&
                        Array.isArray(driverCars) &&
                        driverCars.length > 0
                    ) {
                        carType = driverCars[0].carType || "";
                    } else {
                        // Handle single car (non-multi-service)
                        carType = dataCarsApproved[0][carId].carType || "";
                    }
                }

                if (
                    ifExistCarApproved !== undefined &&
                    ifExistCarApproved !== null
                ) {
                    carApproved = true;
                }

                const newDriver = {
                    ...driver,
                    carType: carType || "",
                    carApproved: settings.multi_service ? true : carApproved,
                };

                console.log("newDriver", newDriver);
                uploadDataDrivers(newDriver);
            }
        }
    }, [dataDrivers, cars, settings.multi_service, uploadDataDrivers]);

    const handleDeleteCar = async (carGroup) => {
        setShowConfirmationDialog(false);
        setShowLoadingProgress(true);

        if (carGroup === null) {
            setShowLoadingProgress(false);
            setSelectedCar(null);
            return;
        }

        try {
            const carId = carGroup.car_id;

            // Delete from Firebase
            const success = await deleteCarFromFirebase(carId);

            if (success) {
                // Data will be automatically updated via Firebase listener
                console.log(
                    "Car deleted successfully, Firebase listener will update data automatically"
                );
            } else {
                console.error("Failed to delete car from Firebase");
            }

            setShowLoadingProgress(false);
        } catch (error) {
            console.log("delete error", error);
            setShowLoadingProgress(false);
        } finally {
            setSelectedCar(null);
        }
    };

    const uploadDataCars = useCallback(
        async (carGroup) => {
            console.log(
                "🔄 uploadDataCars - updating Firebase directly:",
                carGroup
            );

            try {
                // Find the original car data to update using car_id
                const originalCar = cars.find((item) => {
                    const carId = Object.keys(item)[0];
                    return carId === carGroup.car_id;
                });

                if (originalCar) {
                    const carId = Object.keys(originalCar)[0];
                    const carData = originalCar[carId];

                    // Update directly in Firebase - listener will handle state update
                    const { carEditRef } = firebase;
                    const carRef = carEditRef(carData.id);

                    console.log(
                        "🔥 Updating Firebase directly at:",
                        carRef.toString()
                    );
                    await update(carRef, {
                        [carGroup.field]: carGroup[carGroup.field],
                        updatedAt: Date.now(),
                    });

                    // If multi-service, also update individual cars in Firebase
                    if (Array.isArray(carData.cars)) {
                        const carsUpdatePromises = carData.cars.map(
                            async (car, index) => {
                                const carPath = `${carRef.toString()}/cars/${index}`;
                                return update(ref(firebase.database, carPath), {
                                    [carGroup.field]: carGroup[carGroup.field],
                                });
                            }
                        );
                        await Promise.all(carsUpdatePromises);
                    }

                    console.log(
                        "✅ Firebase update completed - listener will update state"
                    );

                    // Don't dispatch Redux action - Firebase listener will handle state update
                    // This prevents double updates and data duplication
                } else {
                    console.error(
                        "❌ Original car not found for car_id:",
                        carGroup.car_id
                    );
                }
            } catch (error) {
                console.error("❌ Error in uploadDataCars:", error);
                // Fallback to Redux if Firebase update fails
                console.log("🔄 Falling back to Redux update...");
                const originalCar = cars.find((item) => {
                    const carId = Object.keys(item)[0];
                    return carId === carGroup.car_id;
                });

                if (originalCar) {
                    const carId = Object.keys(originalCar)[0];
                    const carData = originalCar[carId];
                    const updatedCarData = {
                        ...carData,
                        [carGroup.field]: carGroup[carGroup.field],
                    };

                    if (Array.isArray(carData.cars)) {
                        const updatedCars = carData.cars.map((c) => {
                            return {
                                ...c,
                                [carGroup.field]: carGroup[carGroup.field],
                            };
                        });
                        updatedCarData.cars = updatedCars;
                    }

                    dispatch(editCar(updatedCarData, "Update"));
                }
            }
        },
        [cars, dispatch, editCar]
    );

    // Transform cars data for display with grouping
    const transformCarsForDisplay = () => {
        console.log("transformCarsForDisplay called");
        console.log("cars:", cars);
        console.log("drivers:", drivers);

        if (!cars || cars.length === 0) {
            console.log("No cars data available");
            return [];
        }

        if (!drivers || drivers.length === 0) {
            console.log("No drivers data available");
        }

        const carGroups = [];
        cars.forEach((carGroupObj) => {
            const carId = Object.keys(carGroupObj)[0];
            const carGroup = carGroupObj[carId];
            const driverObj = drivers
                ? drivers.find((d) => d.id === carGroup.driver)
                : null;

            console.log(`🚗 Processing car ${carId}:`, carGroup);
            console.log(`📸 Car images for ${carId}:`, carGroup.car_images);
            console.log(
                `📸 Car images length for ${carId}:`,
                carGroup.car_images?.length
            );
            console.log(`🚗 Cars array for ${carId}:`, carGroup.cars);
            console.log(`🚗 Cars length for ${carId}:`, carGroup.cars?.length);

            // Log detailed car_images structure
            if (carGroup.car_images && Array.isArray(carGroup.car_images)) {
                console.log(`📸 Car_images details for ${carId}:`);
                carGroup.car_images.forEach((img, index) => {
                    console.log(`  - Image ${index}:`, img);
                });
            }

            // Count and log actual images
            const actualImageCount = countActualImages(carGroup);
            console.log(
                `📸 Actual image count for ${carId}:`,
                actualImageCount
            );

            // Log old calculation for comparison
            const oldCount =
                ((carGroup.cars && carGroup.cars.length) || 0) +
                ((carGroup.car_images && carGroup.car_images.length) || 0);
            console.log(`📸 Old calculation for ${carId}:`, oldCount);
            console.log(
                `📸 Difference: ${
                    oldCount - actualImageCount
                } extra items counted`
            );

            // Log detailed breakdown
            console.log(`📸 Breakdown for ${carId}:`);
            console.log(`  - Main image: ${carGroup.image ? "YES" : "NO"}`);
            console.log(
                `  - car_images array: ${
                    carGroup.car_images?.length || 0
                } items`
            );
            console.log(`  - cars array: ${carGroup.cars?.length || 0} items`);
            if (carGroup.car_images && carGroup.car_images.length > 0) {
                console.log(
                    `  - car_images with image property:`,
                    carGroup.car_images.filter((img) => img.image).length
                );
            }
            if (carGroup.cars && carGroup.cars.length > 0) {
                console.log(
                    `  - cars with car_image property:`,
                    carGroup.cars.filter((car) => car.car_image).length
                );
            }

            carGroups.push({
                car_id: carId,
                id: carGroup.id,
                vehicleMake: carGroup.vehicleMake || "",
                vehicleModel: carGroup.vehicleModel || "",
                vehicleNumber: carGroup.vehicleNumber || "",
                driver: driverObj?.desc || t("unknown_driver"),
                driverId: carGroup.driver,
                image:
                    carGroup.car_images?.[0]?.image || carGroup.car_image || "",
                active: !!carGroup.active,
                approved: !!carGroup.approved,
                carType: carGroup.carType || "",
                category: carGroup.category || "",
                createdAt:
                    carGroup.createdAt ?? carGroup.dateCreated ?? Date.now(),
                other_info: carGroup.other_info || "",
                cars: carGroup.cars || [],
                car_images: carGroup.car_images || [], // Add this line to preserve car_images
                for_multi_service: carGroup.for_multi_service || false,
            });
        });

        console.log("Transformed car groups:", carGroups);
        // Sort car groups by createdAt (newest first) to maintain chronological order
        return carGroups.sort((a, b) => {
            return (b.createdAt || 0) - (a.createdAt || 0);
        });
    };

    const transformedCars = transformCarsForDisplay();
    console.log("Cars from state:", cars);
    console.log("Cars length:", cars.length);
    console.log("Transformed cars:", transformedCars);
    console.log("Transformed cars length:", transformedCars.length);

    const filteredCars = transformedCars
        .filter((carGroup) => {
            const term = searchTerm.toLowerCase();
            return (
                (carGroup.driver || "").toLowerCase().includes(term) ||
                (carGroup.category || "").toLowerCase().includes(term) ||
                (carGroup.carType || "").toLowerCase().includes(term) ||
                (carGroup.vehicleNumber || "").toLowerCase().includes(term) ||
                (carGroup.vehicleMake || "").toLowerCase().includes(term) ||
                (carGroup.vehicleModel || "").toLowerCase().includes(term) ||
                (carGroup.other_info || "").toLowerCase().includes(term)
            );
        })
        .sort((a, b) => {
            // Sort by createdAt (newest first) to maintain chronological order
            return (b.createdAt || 0) - (a.createdAt || 0);
        });

    console.log("Filtered cars:", filteredCars);

    // Log final display order for verification
    console.log(
        "📅 Final display order (first 3 cars):",
        filteredCars.slice(0, 3).map((car) => ({
            driver: car.driver,
            createdAt: car.createdAt,
            createdDate: car.createdAt
                ? new Date(car.createdAt).toLocaleString()
                : "No date",
            vehicleNumber: car.vehicleNumber,
        }))
    );

    // Function to count actual images
    function countActualImages(carGroup) {
        let count = 0;
        let details = [];

        // Count car_images with actual images (this includes the main image)
        if (carGroup.car_images && Array.isArray(carGroup.car_images)) {
            const carImagesWithImage = carGroup.car_images.filter(
                (img) => img.image
            );
            count += carImagesWithImage.length;
            details.push(`${carImagesWithImage.length} car_images`);
            console.log(
                "📸 Counting car_images:",
                carImagesWithImage.length,
                "items"
            );
            carImagesWithImage.forEach((img, index) => {
                console.log(`  - car_images[${index}]:`, img.image);
            });
        }

        // Count cars with images
        if (carGroup.cars && Array.isArray(carGroup.cars)) {
            const carsWithImage = carGroup.cars.filter((car) => car.car_image);
            count += carsWithImage.length;
            details.push(`${carsWithImage.length} cars`);
            console.log("📸 Counting cars:", carsWithImage.length, "items");
            carsWithImage.forEach((car, index) => {
                console.log(`  - cars[${index}]:`, car.car_image);
            });
        }

        console.log("📸 Total count:", count, "(", details.join(", "), ")");
        return count;
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleImageClick = (carGroup) => {
        console.log("🔍 handleImageClick - carGroup:", carGroup);

        // Find the original car data from Firebase
        const originalCarData = cars.find((carObj) => {
            const carId = Object.keys(carObj)[0];
            return carId === carGroup.car_id;
        });

        if (!originalCarData) {
            console.log("❌ Original car data not found");
            return;
        }

        const carId = Object.keys(originalCarData)[0];
        const originalCar = originalCarData[carId];

        console.log("🔍 Original car data:", originalCar);
        console.log("📸 Original car_images:", originalCar.car_images);
        console.log(
            "📸 Original car_images length:",
            originalCar.car_images?.length
        );
        console.log("🚗 Original cars array:", originalCar.cars);
        console.log("🚗 Original cars length:", originalCar.cars?.length);

        // Log detailed original data
        if (originalCar.car_images && Array.isArray(originalCar.car_images)) {
            console.log("📸 Original car_images details:");
            originalCar.car_images.forEach((img, index) => {
                console.log(`  - car_images[${index}]:`, img);
                console.log(`    has image property:`, !!img.image);
            });
        }

        if (originalCar.cars && Array.isArray(originalCar.cars)) {
            console.log("🚗 Original cars details:");
            originalCar.cars.forEach((car, index) => {
                console.log(`  - cars[${index}]:`, car);
                console.log(`    has car_image property:`, !!car.car_image);
            });
        }

        // Get all images for this car
        const images = [];

        // Add car_images array if exists (this includes all car images)
        if (originalCar.car_images && Array.isArray(originalCar.car_images)) {
            console.log("🖼️ Car_images array:", originalCar.car_images);
            originalCar.car_images.forEach((img, index) => {
                if (img.image) {
                    console.log(
                        `📸 Adding car_images ${index + 1}:`,
                        img.image
                    );
                    images.push({
                        url: img.image,
                        type: "gallery",
                        description: `Gallery image ${index + 1}`,
                    });
                }
            });
        }

        // Add cars array if exists
        if (originalCar.cars && Array.isArray(originalCar.cars)) {
            console.log("🚗 Cars array:", originalCar.cars);
            originalCar.cars.forEach((car, index) => {
                if (car.car_image) {
                    console.log(
                        `📸 Adding car ${index + 1} image:`,
                        car.car_image
                    );
                    images.push({
                        url: car.car_image,
                        type: "car",
                        description: `Car ${index + 1} image`,
                    });
                }
            });
        }

        console.log("📸 Total images found:", images.length);
        console.log("📸 Images array:", images);

        if (images.length > 0) {
            setSelectedCarImages(images);
            setCurrentImageIndex(0);
            setImageModalOpen(true);
        } else {
            console.log("❌ No images found for this car");
            alert(t("no_images_available"));
        }
    };

    const handleNextImage = useCallback(() => {
        if (currentImageIndex < selectedCarImages.length - 1) {
            setCurrentImageIndex(currentImageIndex + 1);
        }
    }, [currentImageIndex, selectedCarImages.length]);

    const handlePrevImage = useCallback(() => {
        if (currentImageIndex > 0) {
            setCurrentImageIndex(currentImageIndex - 1);
        }
    }, [currentImageIndex]);

    const handleCloseImageModal = () => {
        setImageModalOpen(false);
        setSelectedCarImages([]);
        setCurrentImageIndex(0);
    };

    const handleDownloadImage = () => {
        if (selectedCarImages[currentImageIndex]?.url) {
            const link = document.createElement("a");
            link.href = selectedCarImages[currentImageIndex].url;
            link.target = "_blank";
            link.download = `car_image_${Date.now()}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!imageModalOpen) return;

            switch (event.key) {
                case "ArrowLeft":
                    event.preventDefault();
                    handlePrevImage();
                    break;
                case "ArrowRight":
                    event.preventDefault();
                    handleNextImage();
                    break;
                case "Escape":
                    event.preventDefault();
                    handleCloseImageModal();
                    break;
                default:
                    break;
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [
        imageModalOpen,
        currentImageIndex,
        selectedCarImages.length,
        handleNextImage,
        handlePrevImage,
    ]);

    // Calculate paginated data
    const paginatedCars = filteredCars.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    console.log("Paginated cars:", paginatedCars);

    return (
        <Box sx={{ p: 3, width: "100%" }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Typography variant="h4" gutterBottom>
                    {t("cars_list")} {loading && `(${t("loading")}...)`}
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                    <ElevatedButton
                        onClick={() => {
                            setShowAddCarDialog(true);
                        }}
                    >
                        {t("add_car")}
                    </ElevatedButton>
                </Box>
            </Box>

            {/* search bar */}
            <TextField
                label={t("search")}
                variant="outlined"
                fullWidth
                sx={{ mb: 2 }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />

            <TableContainer component={Paper}>
                <Table sx={{ "& td, & th": { verticalAlign: "top" } }}>
                    <TableHead sx={{ backgroundColor: colors.MAIN_COLOR }}>
                        <TableRow>
                            <TableCell
                                sx={{ color: "white", fontWeight: "bold" }}
                            >
                                {t("date_created")}
                            </TableCell>
                            <TableCell
                                sx={{ color: "white", fontWeight: "bold" }}
                            >
                                {t("driver")}
                            </TableCell>
                            <TableCell
                                sx={{ color: "white", fontWeight: "bold" }}
                            >
                                {t("image")}
                            </TableCell>
                            <TableCell
                                sx={{ color: "white", fontWeight: "bold" }}
                            >
                                {t("category")}
                            </TableCell>
                            <TableCell
                                sx={{ color: "white", fontWeight: "bold" }}
                            >
                                {t("car_type")}
                            </TableCell>
                            <TableCell
                                sx={{ color: "white", fontWeight: "bold" }}
                            >
                                {t("vehicle_number")}
                            </TableCell>
                            <TableCell
                                sx={{ color: "white", fontWeight: "bold" }}
                            >
                                {t("vehicle_make")}
                            </TableCell>
                            <TableCell
                                sx={{ color: "white", fontWeight: "bold" }}
                            >
                                {t("vehicle_model")}
                            </TableCell>
                            <TableCell
                                sx={{ color: "white", fontWeight: "bold" }}
                            >
                                {t("other_info")}
                            </TableCell>
                            <TableCell
                                sx={{ color: "white", fontWeight: "bold" }}
                            >
                                {t("active")}
                            </TableCell>

                            <TableCell
                                sx={{ color: "white", fontWeight: "bold" }}
                            >
                                {t("approved")}
                            </TableCell>
                            <TableCell
                                sx={{
                                    color: "white",
                                    fontWeight: "bold",
                                    textAlign: "center",
                                }}
                            >
                                {t("action")}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedCars.map((carGroup) => {
                            const approved = carGroup.approved;
                            const active = carGroup.active;

                            return (
                                <TableRow key={carGroup.car_id}>
                                    <TableCell>
                                        {carGroup.createdAt &&
                                        !isNaN(
                                            new Date(
                                                carGroup.createdAt
                                            ).getTime()
                                        )
                                            ? format(
                                                  new Date(carGroup.createdAt),
                                                  "MM/dd/yyyy"
                                              )
                                            : t("n_a")}
                                    </TableCell>
                                    <TableCell>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                            }}
                                        >
                                            {carGroup.driver ||
                                                t("unknown_driver")}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns:
                                                    "repeat(2, 1fr)",
                                                gap: 0.5,
                                                width: "fit-content",
                                                maxWidth: "80px",
                                            }}
                                        >
                                            {/* Show all car images as small thumbnails in 2x grid */}
                                            {(() => {
                                                const images = [];

                                                // Add car_images if exists (this includes all car images)
                                                if (
                                                    carGroup.car_images &&
                                                    Array.isArray(
                                                        carGroup.car_images
                                                    )
                                                ) {
                                                    carGroup.car_images.forEach(
                                                        (img) => {
                                                            if (img.image) {
                                                                images.push({
                                                                    url: img.image,
                                                                    type: "gallery",
                                                                });
                                                            }
                                                        }
                                                    );
                                                }

                                                // Add cars with images if exists
                                                if (
                                                    carGroup.cars &&
                                                    Array.isArray(carGroup.cars)
                                                ) {
                                                    carGroup.cars.forEach(
                                                        (car) => {
                                                            if (car.car_image) {
                                                                images.push({
                                                                    url: car.car_image,
                                                                    type: "car",
                                                                });
                                                            }
                                                        }
                                                    );
                                                }

                                                return images.map(
                                                    (image, index) => (
                                                        <Avatar
                                                            key={index}
                                                            variant="rounded"
                                                            src={image.url}
                                                            alt={`Car ${
                                                                index + 1
                                                            }`}
                                                            sx={{
                                                                width: 25,
                                                                height: 18,
                                                                cursor: "pointer",
                                                                border: "1px solid #e0e0e0",
                                                            }}
                                                            onClick={() =>
                                                                handleImageClick(
                                                                    carGroup
                                                                )
                                                            }
                                                        />
                                                    )
                                                );
                                            })()}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {carGroup.cars &&
                                            (() => {
                                                const uniqueCategories =
                                                    Array.from(
                                                        new Set(
                                                            carGroup.cars.map(
                                                                (car) =>
                                                                    car.category
                                                            )
                                                        )
                                                    );
                                                return (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        {uniqueCategories.map(
                                                            (category) => (
                                                                <Box
                                                                    key={
                                                                        category
                                                                    }
                                                                    sx={{
                                                                        display:
                                                                            "inline-block",
                                                                        whiteSpace:
                                                                            "nowrap",
                                                                        px: 1.5,
                                                                        py: 0.5,
                                                                        borderRadius:
                                                                            "12px",
                                                                        backgroundColor:
                                                                            "#e0e0e0",
                                                                        color: "#333",
                                                                        fontSize:
                                                                            "0.8rem",
                                                                        fontWeight: 500,
                                                                        mr: 0.5,
                                                                    }}
                                                                >
                                                                    {t(
                                                                        category
                                                                    )}
                                                                </Box>
                                                            )
                                                        )}
                                                    </Box>
                                                );
                                            })()}
                                    </TableCell>
                                    <TableCell>
                                        {carGroup.cars && (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: 0.5,
                                                }}
                                            >
                                                {carGroup.cars.map((car) => (
                                                    <Box
                                                        key={car.carType}
                                                        sx={{
                                                            display:
                                                                "inline-block",
                                                            whiteSpace:
                                                                "nowrap",
                                                            px: 1.5,
                                                            py: 0.5,
                                                            borderRadius:
                                                                "12px",
                                                            backgroundColor:
                                                                "#e0e0e0",
                                                            color: "#333",
                                                            fontSize: "0.8rem",
                                                            fontWeight: 500,
                                                            mr: 0.5,
                                                        }}
                                                    >
                                                        {car.carType}
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {carGroup.vehicleNumber || ""}
                                    </TableCell>
                                    <TableCell>
                                        {carGroup.vehicleMake || ""}
                                    </TableCell>
                                    <TableCell>
                                        {carGroup.vehicleModel || ""}
                                    </TableCell>
                                    <TableCell>
                                        {carGroup.other_info || ""}
                                    </TableCell>
                                    <TableCell>
                                        <Checkbox
                                            className={classes.checkbox}
                                            checked={active}
                                            onChange={(e) => {
                                                handleApprove(
                                                    carGroup,
                                                    "active",
                                                    e.target.checked
                                                );
                                            }}
                                            disabled={false}
                                        />
                                    </TableCell>

                                    <TableCell>
                                        <Checkbox
                                            className={classes.checkbox}
                                            checked={approved}
                                            onChange={(e) => {
                                                handleApprove(
                                                    carGroup,
                                                    "approved",
                                                    e.target.checked
                                                );
                                            }}
                                            disabled={false}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1,
                                            }}
                                        >
                                            <ElevatedButton
                                                type={
                                                    approved
                                                        ? "error"
                                                        : "success"
                                                }
                                                onClick={() => {
                                                    handleApprove(
                                                        carGroup,
                                                        "approved",
                                                        !approved
                                                    );
                                                }}
                                            >
                                                {approved
                                                    ? t("reject")
                                                    : t("approved")}
                                            </ElevatedButton>
                                            <ElevatedButton
                                                type="info"
                                                onClick={() => {
                                                    setSelectedCar(carGroup);
                                                    setShowEditCarDialog(true);
                                                }}
                                            >
                                                {t("edit")}
                                            </ElevatedButton>
                                            <ElevatedButton
                                                type="error"
                                                onClick={() => {
                                                    if (selectedCar !== null) {
                                                        setSelectedCar(null);
                                                    }
                                                    setSelectedCar(carGroup);
                                                    setShowConfirmationDialog(
                                                        true
                                                    );
                                                }}
                                            >
                                                {t("delete")}
                                            </ElevatedButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={filteredCars.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[5]}
                    sx={{
                        ".MuiTablePagination-select": {
                            display: "none",
                        },
                    }}
                />
            </TableContainer>

            <ConfirmationDialog
                open={showConfirmationDialog}
                onClose={() => setShowConfirmationDialog(false)}
                onConfirm={() => handleDeleteCar(selectedCar)}
                title={t("confirm")}
                message={t("are_you_sure_you_want_to_delete_this_car")}
            />
            <AddCarDialog
                open={showAddCarDialog}
                onClose={() => setShowAddCarDialog(false)}
            />
            <EditCarDialog
                open={showEditCarDialog}
                onClose={() => {
                    setShowEditCarDialog(false);
                    setSelectedCar(null);
                }}
                carData={selectedCar}
            />
            <LoadingProgress
                open={showLoadingProgress}
                message={t("please_wait")}
            />

            {/* Image Modal */}
            <Modal
                open={imageModalOpen}
                onClose={handleCloseImageModal}
                className={classes.modal}
            >
                <div className={classes.modalPaper}>
                    <div className={classes.modalHeader}>
                        <Typography variant="h6">
                            {t("car_images")}
                            {selectedCarImages.length > 1 && (
                                <span
                                    style={{
                                        fontSize: "0.875rem",
                                        color: "#666",
                                        marginLeft: 8,
                                    }}
                                >
                                    ({currentImageIndex + 1} of{" "}
                                    {selectedCarImages.length})
                                </span>
                            )}
                        </Typography>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                            }}
                        >
                            <IconButton
                                onClick={handleDownloadImage}
                                title={t("download")}
                            >
                                <DownloadIcon />
                            </IconButton>
                            <IconButton onClick={handleCloseImageModal}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                    </div>

                    <div className={classes.modalContent}>
                        {selectedCarImages.length > 0 && (
                            <div className={classes.imageContainer}>
                                {selectedCarImages.length > 1 && (
                                    <>
                                        <IconButton
                                            className={`${classes.navigationButton} ${classes.prevButton}`}
                                            onClick={handlePrevImage}
                                            disabled={currentImageIndex === 0}
                                        >
                                            <NavigateBeforeIcon />
                                        </IconButton>
                                        <IconButton
                                            className={`${classes.navigationButton} ${classes.nextButton}`}
                                            onClick={handleNextImage}
                                            disabled={
                                                currentImageIndex ===
                                                selectedCarImages.length - 1
                                            }
                                        >
                                            <NavigateNextIcon />
                                        </IconButton>
                                        <div className={classes.imageCounter}>
                                            {currentImageIndex + 1} /{" "}
                                            {selectedCarImages.length}
                                        </div>
                                    </>
                                )}

                                <img
                                    src={
                                        selectedCarImages[currentImageIndex]
                                            ?.url
                                    }
                                    alt={
                                        selectedCarImages[currentImageIndex]
                                            ?.description || "Car image"
                                    }
                                    className={classes.carImage}
                                    onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display =
                                            "block";
                                    }}
                                />
                                <div
                                    style={{
                                        display: "none",
                                        textAlign: "center",
                                        padding: "50px",
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        style={{ color: "#999" }}
                                    >
                                        {t("image_not_available")}
                                    </Typography>
                                </div>

                                {selectedCarImages[currentImageIndex] && (
                                    <div
                                        style={{
                                            marginTop: 16,
                                            padding: "16px",
                                            backgroundColor: "#f5f5f5",
                                            borderRadius: "8px",
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            style={{
                                                marginBottom: 8,
                                                color: colors.MAIN_COLOR,
                                            }}
                                        >
                                            {
                                                selectedCarImages[
                                                    currentImageIndex
                                                ].description
                                            }
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            style={{ color: "#666" }}
                                        >
                                            {t("image_type")}:{" "}
                                            {
                                                selectedCarImages[
                                                    currentImageIndex
                                                ].type
                                            }
                                        </Typography>
                                    </div>
                                )}

                                {/* Thumbnail Navigation */}
                                {selectedCarImages.length > 1 && (
                                    <div
                                        style={{
                                            marginTop: 16,
                                            display: "flex",
                                            gap: 8,
                                            justifyContent: "center",
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        {selectedCarImages.map(
                                            (image, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        width: 60,
                                                        height: 40,
                                                        borderRadius: 1,
                                                        overflow: "hidden",
                                                        cursor: "pointer",
                                                        border:
                                                            index ===
                                                            currentImageIndex
                                                                ? `2px solid ${colors.MAIN_COLOR}`
                                                                : "2px solid transparent",
                                                        opacity:
                                                            index ===
                                                            currentImageIndex
                                                                ? 1
                                                                : 0.7,
                                                        "&:hover": {
                                                            opacity: 1,
                                                        },
                                                    }}
                                                    onClick={() =>
                                                        setCurrentImageIndex(
                                                            index
                                                        )
                                                    }
                                                >
                                                    <img
                                                        src={image.url}
                                                        alt={`Thumbnail ${
                                                            index + 1
                                                        }`}
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                        }}
                                                        onError={(e) => {
                                                            e.target.style.display =
                                                                "none";
                                                        }}
                                                    />
                                                </Box>
                                            )
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Modal>
        </Box>
    );
};

export default CarsListMultiService;
